import { RefreshIcon, MagicIcon } from 'src/assets/icons';
import { Close } from '@poli-digital/react';
import { UncontrolledTooltip, Button } from '@polichat/flamboyant';
import { toast } from 'react-toastify';

// css module
import styles from './copilot.module.css';
import { useCopilotAnswer } from '../../../contexts/copilotAnswer/context';
import { useTrigger } from 'src/hooks/useTrigger';
import { useSelector } from 'react-redux';
import { useFlags } from 'flagsmith/react';
import { customUseFlags } from 'src/hooks/customUseFlags';
import { useEffect } from 'react';
import { useGlotaT } from 'src/hooks/useGlotaTranslation';

export function CopilotAnswerComponent({
  isWindow24Open,
  onSelectSuggestion,
  onOpen,
  onClose,
  isEnabledByUserSettings,
}: {
  onSelectSuggestion: (value: string) => void;
  onOpen?: () => void;
  onClose?: () => void;
  isWindow24Open: boolean;
  isEnabledByUserSettings: boolean;
}) {
  const {
    input,
    isOpen,
    isLoading,
    error,
    retryRequest,
    handleOpen,
    handleCloseByUserAction,
  } = useCopilotAnswer();
  const flag = useFlags(['ia_suggestion']);
  const ia_suggestion = customUseFlags(flag.ia_suggestion);

  const { sendTrigger, triggerTypes } = useTrigger();

  const currentChat = useSelector((state: any) =>
    state?.chat?.chats?.find(
      (chat: any) => chat.id === state.chat.chatSelectedId
    )
  );

  function handleRefreshClick({
    sendTriggerOnCreate,
  }: {
    sendTriggerOnCreate?: boolean;
  }) {
    if (isLoading) {
      return;
    }

    retryRequest(undefined, sendTriggerOnCreate);
  }

  const isShowingModal = isOpen && isEnabledByUserSettings;

  const suggestionWithAIText = useGlotaT('spa.chat.copilot.suggestionWithAI');
  const tooltipEnabledText = useGlotaT('spa.chat.copilot.tooltipEnabled');
  const tooltipDisabledText = useGlotaT('spa.chat.copilot.tooltipDisabled');
  const errorGeneratingText = useGlotaT('spa.chat.copilot.errorGenerating');
  const errorTitleText = useGlotaT('spa.chat.copilot.errorTitle');
  const tryAgainText = useGlotaT('spa.chat.copilot.tryAgain');
  const generateNewText = useGlotaT('spa.chat.copilot.generateNew');
  const closeSuggestionText = useGlotaT('spa.chat.copilot.closeSuggestion');

  useEffect(() => {
    if (isShowingModal && error) {
      toast.error(errorGeneratingText);
    }
  }, [error, errorGeneratingText]);

  if (isWindow24Open || !ia_suggestion) return null;
  return (
    <div
      className={`${styles.copilotBar} ${
        isShowingModal ? styles.copilotBarOpen : ''
      }`}
    >
      <div
        className={styles.floatingBtnStyle}
        id="openSuggestionButton"
        onClick={() => {
          if (!isEnabledByUserSettings) {
            return;
          }
          handleOpen({ variant: 'createAnswer' });

          if (onOpen) {
            onOpen();
          }
        }}
      >
        <UncontrolledTooltip placement="right" target="openSuggestionButton">
          {isEnabledByUserSettings ? (
            <>
              {tooltipEnabledText}
            </>
          ) : (
            <>{tooltipDisabledText}</>
          )}
        </UncontrolledTooltip>
        <span role="img" aria-label="copilot">
          <MagicIcon />
        </span>
        {suggestionWithAIText}
      </div>

      {isShowingModal && (
        <>
          {/* Container de erro */}
          {error ? (
            <div className={styles.errorContainer}>
              <span className={styles.errorMessage}>
                {errorTitleText}
              </span>

              <Button
                outline
                color="primary"
                className={styles.retryButton}
                onClick={() =>
                  handleRefreshClick({ sendTriggerOnCreate: true })
                }
              >
                {isLoading && <RefreshIcon className={styles.reloadIcon} />}

                <span style={{ opacity: isLoading ? '0' : '1' }}>
                  {tryAgainText}
                </span>
              </Button>
            </div>
          ) : (
            <>
              <div
                className={styles.body}
                onClick={() => {
                  onSelectSuggestion(input);

                  if (currentChat?.id) {
                    sendTrigger({
                      entity_id: currentChat?.id,
                      entity_type: 'contact',
                      type: triggerTypes.SELECTED,
                      origin: 'antiga-interface',
                      payload: {
                        component: 'CopilotAnswerComponent',
                        hook: null,
                        element: 'selected-copilot-suggestion',
                      },
                    });
                  }
                }}
              >
                <span className={styles.suggestionText}>{input}</span>
              </div>

              <div className={styles.actions}>
                <button
                  type="button"
                  className={styles.refreshButton}
                  disabled={isLoading}
                  onClick={() => {
                    handleRefreshClick({ sendTriggerOnCreate: false });
                    if (currentChat?.id) {
                      sendTrigger({
                        entity_id: currentChat?.id,
                        entity_type: 'contact',
                        type: triggerTypes.CLICKED,
                        origin: 'antiga-interface',
                        payload: {
                          component: 'CopilotAnswerComponent',
                          hook: null,
                          element: 'button-clicked-generate-new-suggestion',
                        },
                      });
                    }
                  }}
                  id="btnRefreshSuggestion"
                >
                  <UncontrolledTooltip
                    placement="top"
                    target="btnRefreshSuggestion"
                  >
                    {generateNewText}
                  </UncontrolledTooltip>
                  <RefreshIcon className={isLoading ? styles.reloadIcon : ''} />
                </button>

                <button
                  onClick={() => {
                    handleCloseByUserAction();
                    if (currentChat?.id) {
                      sendTrigger({
                        entity_id: currentChat?.id,
                        entity_type: 'contact',
                        type: triggerTypes.CLOSED,
                        origin: 'antiga-interface',
                        payload: {
                          component: 'CopilotAnswerComponent',
                          hook: null,
                          element: 'close-button-clicked-suggestion-message',
                        },
                      });
                    }

                    if (onClose) {
                      onClose();
                    }
                  }}
                  className={styles.btnClose}
                  id="btnCloseSuggestion"
                >
                  <UncontrolledTooltip
                    placement="top"
                    target="btnCloseSuggestion"
                  >
                    {closeSuggestionText}
                  </UncontrolledTooltip>
                  <Close width={12} height={12} />
                </button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
